'use strict';

var FileDownloader = require('./FileDownloader');
require('./KayleeService');
var log = require('../../log/Log').channel('KnoxService');
require('./SharedPrefs');

/* global AveoAutostart */
/* global AveoDevice */
/* global AveoKnox */
/* global AveoPackageInfo */

angular.module('inara').factory('KnoxService', KnoxService);

KnoxService.$inject = ['$q', 'SharedPrefs', 'KayleeService'];

function KnoxService($q, SharedPrefs, KayleeService) {
    var elmLicenseActivated = false;
    var elmKeyVersion = 0;
    var klmLicenseActivated = false;
    var klmKeyVersion = 0;
    var serialNumber = null;
    var allowOtaUpgrade = null;
    var kioskMode = null;
    var inaraInfo = null;
    var firstTimeInaraInfoChanged = true;

    return {
        initialize: initialize,
        name: 'knox',
        caps: {
            hasAllowOtaUpgrade: true,
            hasBattery: true,
            hasKioskModeEnable: true,
            hasRestartDevice: false,
            hasRestartInara: true,
        },
        cleanUpFiles: cleanUpFiles,
        enableAutostart: enableAutostart,
        getFactoryInstallChecklist: getFactoryInstallChecklist,
        getSerialNumber: function() { return serialNumber; },
        // No required permissions because we've already shipped without them.
        // If we were to require them now, users would get prompted. See
        // SNTY-1528 for more information.
        setAllowOtaUpgrade: setAllowOtaUpgrade,
        setEthernetState: setEthernetState,
        setKioskMode: setKioskMode,
        uninstallApplication: uninstallApplication,
        updateApplication: updateApplication,
        updateKnoxAnimations: updateKnoxAnimations
    };

    function getFactoryInstallChecklist() {
        return [{
            name: 'Inara device administrator',
            getState: function() {
                return AveoDevice.getDeviceAdminActiveForComponent(
                    'com.aveosystems.inara', 'com.aveosystems.AveoDeviceDeviceAdminReceiver')
                .then(function(deviceAdminActive) {
                    return deviceAdminActive ? 'yes' : 'no';
                });
            },
        }, {
            name: 'Inara is launcher',
            getState: function() {
                return AveoDevice.getLauncher()
                .then(function(packageName) {
                    return packageName === 'com.aveosystems.inara' ? 'yes' : 'no';
                });
            },
        }, {
            name: 'Lava Tablet Manager installed',
            getState: function() {
                return $q(function(resolve, reject) {
                    AveoPackageInfo.getPackageInfo('com.lava.tabletmanager7',
                        function(pinfo) { resolve('yes'); },
                        function(err) { resolve('no'); }
                    );
                });
            },
        }, {
            name: 'Lava Tablet Manager device administrator',
            getState: function() {
                return AveoDevice.getDeviceAdminActiveForComponent(
                    'com.lava.tabletmanager7', 'com.lava.tabletmanager7.DeviceAdmin')
                .then(function(isActive) {
                    return isActive ? 'yes' : 'no';
                });
            },
        }, {
            name: 'Lava config file installed',
            getState: function() {
                return SharedPrefs.getInt('lavaConfigFileVersion', 0)
                .then(function(version) {
                    return version !== 0 ? 'yes' : 'no';
                });
            },
        }, {
            name: 'Kiosk mode enabled',
            getState: function() {
                return $q.resolve(kioskMode ? 'yes' : 'no');
            },
        }];
    }

    function initialize(platformInfo) {
        log.debug('initializing');

        return registerLicenseHandler()
        .then(function() {
            log.debug('getting prefs');
            return $q.all([
                SharedPrefs.getBoolean('elmLicenseActivated', false),
                SharedPrefs.getInt('elmKeyVersion', 0),
                SharedPrefs.getBoolean('klmLicenseActivated', false),
                SharedPrefs.getInt('klmKeyVersion', 0)
            ]);
        })
        .then(function(data) {
            elmLicenseActivated = data[0];
            elmKeyVersion = data[1];
            klmLicenseActivated = data[2];
            klmKeyVersion = data[3];
            log.debug('elmLicenseActivated = ' + elmLicenseActivated);
            log.debug('elmKeyVersion = ' + elmKeyVersion);
            log.debug('klmLicenseActivated = ' + klmLicenseActivated);
            log.debug('klmKeyVersion = ' + klmKeyVersion);

            // Assume that whoever called initialize() made sure KayleeService
            // was ready.

            // The current database rules allow anonymous access to the inara
            // info. See SNTY-738.
            log.debug('getting inaraInfo');
            inaraInfo = KayleeService.getInaraInfo();
            inaraInfo.$onChange(onInaraInfoChanged);
            return inaraInfo.$loaded();
        })
        .then(activateElmLicense)
        .then(activateKlmLicense)
        .then(loadSerialNumber)
        .then(function() { return setKioskMode(true); })
        .then(function() { return setChargerConnectionSoundEnabledState(false); })
        .then(removeLockScreen)
        .then(function() { return setAllowOtaUpgrade(false); })
        .then(function() {
            return setInputMethod()
            .then($q.resolve, $q.resolve);
        })
        .then(function() {
            return setSystemSoundsEnabled(false)
            .then($q.resolve, $q.resolve);
        })
        .then(function() {
            return setKeyboardMode('KEYBOARD_MODE_SETTINGS_OFF')
            .then($q.resolve, $q.resolve);
        });
    }

    function registerLicenseHandler() {
        log.debug('registering license handler');
        return $q(function(resolve, reject) {
            AveoKnox.setLicenseListener(function(info) {
                var message = "KNOX ELM license event: " + JSON.stringify(info);

                if (parseInt(info.errorCode) !== 0) {
                    log.error(message);
                    elmLicenseActivated = false;
                    SharedPrefs.setBoolean('elmLicenseActivated', elmLicenseActivated);
                } else {
                    log.info(message);
                    // activate callback sets prefs on success
                }
            });

            resolve();
        });
    }

    function onInaraInfoChanged() {
        if (firstTimeInaraInfoChanged) {
            log.debug('ignoring inara info changed since first time');
            firstTimeInaraInfoChanged = false;
            return;
        }

        log.debug('onInaraInfoChanged');

        var promises = [];

        if (inaraInfo.knox.elmKeyVersion > elmKeyVersion) {
            log.info('newer ELM key detected');
            elmLicenseActivated = false;
            promises.push(SharedPrefs.setBoolean('elmLicenseActivated', false));
            promises.push(SharedPrefs.setInt('elmKeyVersion', inaraInfo.knox.elmKeyVersion));
        }

        if (inaraInfo.knox.hasOwnProperty('klmKeyVersion') &&
            inaraInfo.knox.klmKeyVersion > klmKeyVersion) {
            klmLicenseActivated = false;
            promises.push(SharedPrefs.setBoolean('klmKeyActivated', false));
        }

        if (promises.length <= 0) return;

        $q.all(promises)
        .then(activateElmLicense)
        .then(activateKlmLicense)
        .then(loadSerialNumber)
        .then(function() { return setKioskMode(true); })
        .then(function() { return setAllowOtaUpgrade(false); });
    }

    function activateElmLicense() {
        log.info('activating ELM license');

        if (elmLicenseActivated) {
            log.info('ELM license already activated');
            return $q.resolve();
        }

        elmKeyVersion = inaraInfo.knox.elmKeyVersion;
        SharedPrefs.setInt('elmKeyVersion', elmKeyVersion);
        return $q(function(resolve, reject) {
            AveoKnox.activateElmLicense(inaraInfo.knox.elmKey, function() {
                log.info('ELM license activated');
                elmLicenseActivated = true;
                SharedPrefs.setBoolean('elmLicenseActivated', elmLicenseActivated)
                .finally(resolve);
            }, function(err) {
                log.error('failed activating ELM license, error: ' + err);
                reject(err);
            });
        });
    }

    function activateKlmLicense() {
        log.info('activating KLM license');

        if (klmLicenseActivated) {
            log.info('KLM license already activated');
            return $q.resolve();
        }

        if (!inaraInfo.knox.hasOwnProperty('klmKey')) {
            log.info('no KLM key in database, not activating KLM license');
        }

        klmKeyVersion = inaraInfo.knox.klmKeyVersion;
        SharedPrefs.setInt('klmKeyVersion', klmKeyVersion);
        return $q(function(resolve, reject) {
            AveoKnox.activateKlmLicense(inaraInfo.knox.klmKey, function() {
                log.info('KLM license activated');
                klmLicenseActivated = true;
                SharedPrefs.setBoolean('klmLicenseActivated', klmLicenseActivated)
                .finally(resolve);
            }, function(err) {
                log.error('failed activating KLM license, error: ' + err);
                reject(err);
            });
        });
    }

    function enableAutostart() {
        // Disable autostart on Knox, since autostart is handled by setting the
        // application as the default launcher.
        return AveoAutostart.enableAutostart(false);
    }

    function loadSerialNumber() {
        log.info('loading serial number');
        return $q(function(resolve, reject) {
            AveoKnox.getSerialNumber(function(num) {
                serialNumber = num;
                resolve();
            }, function(err) {
                log.error('failed getting serial number, error: ' + err);
                serialNumber = null;
                reject();
            });
        });
    }

    function setKioskMode(enabled) {
        if (kioskMode === enabled) {
            log.info('kiosk mode already set to ' + enabled);
            return $q.resolve();
        }

        log.info('setting kiosk mode to ' + enabled);

        return setStandardKioskMode(false)
        .then(getProKioskState, function(err) { return getProKioskState(); })
        .then(function(proKioskState) {
            if (proKioskState !== enabled) {
                return setProKioskState(enabled, '28362774');
            }
        }, function(err) {
            return setProKioskState(enabled, '28362774');
        })
        .finally(function() { setForceAutoStartUpState(false); })
        .finally(function() { setAllowHardwareKeys(!enabled); })
        .finally(function() {
            if (!enabled) return;

            return $q.resolve()
            .finally(function() { setHardKeyIntentState(false); })
            .finally(function() { setPowerDialogOptionMode('off'); })
            .finally(function() { setHideAllNotificationMessages(true); })
            .finally(function() { setHomeActivity('com.aveosystems.inara'); })
            .finally(function() { setInputMethodRestrictionState(true); })
            .finally(function() { setStatusBarHidden(true); })
            .finally(function() { setAutoRotationState(true, 90); })
            .finally(function() { setToastEnabledState(false); });
        })
        .finally(function() { kioskMode = enabled; })
        .then($q.resolve, $q.resolve);
    }

    function setStandardKioskMode(enabled) {
        log.info('setting standardKioskMode to ' + enabled);

        return $q(function(resolve, reject) {
            AveoKnox.setKioskMode(enabled, function() {
                resolve();
            }, function(err) {
                log.error('failed setting kiosk mode to ' + enabled +
                    ', error: ' + err);
                reject(err);
            });
        });
    }

    function setAllowOtaUpgrade(enabled) {
        log.info('setting allowOtaUpgrade to ' + enabled);

        if (allowOtaUpgrade === enabled) {
            log.info('allowOtaUpgrade already set to ' + enabled);
            return $q.resolve();
        }

        return $q(function(resolve, reject) {
            AveoKnox.setAllowOtaUpgrade(enabled, function() {
                allowOtaUpgrade = enabled;
                resolve();
            }, function(err) {
                log.error('failed setting allowOtaUpgrade to ' + enabled +
                    ', error: ' + err);
                reject(err);
            });
        });
    }

    function setAllowHardwareKeys(allow) {
        log.info('setting allowHardwareKeys to ' + allow);

        return $q(function(resolve, reject) {
            AveoKnox.setAllowHardwareKeys(allow, function() {
                resolve();
            }, function(err) {
                log.error('failed setting allowHardwareKeys to ' + allow +
                    ', error: ' + err);
                reject(err);
            });
        });
    }

    function updateApplication(update, onProgress) {
        var dest = cordova.file.externalDataDirectory + update.dest;

        return FileDownloader.download(update.apkUri, dest, onProgress)
        .then(function() {
            return $q(function(resolve, reject) {
                // Get absolute path.
                // FIXME: This should probably be done with
                // window.resolveLocalFileSystemURL, but the implementation is
                // quirky, so for now I'm hacking it by just removing 'file://'
                // from the beginning.

                var apkPath = dest.slice(7);

                AveoKnox.updateApplication(apkPath, function() {
                    resolve();
                }, function(errorMessage) {
                    reject(errorMessage);
                });
            });
        });
    }

    function uninstallApplication(update) {
        return $q(function(resolve, reject) {
            AveoKnox.uninstallApplication(update.packageName, false, function() {
                resolve();
            }, function(errorMessage) {
                reject(errorMessage);
            });
        });
    }

    // knox custom -> pro kiosk

    function getProKioskState() {
        return $q(function(resolve, reject) {
            AveoKnox.getProKioskState(function(enabled) {
                resolve(enabled);
            }, function(err) {
                log.error('failed getting proKioskState, error: ' + err);
                reject(err);
            });
        });
    }

    function setProKioskState(enable, passCode) {
        log.info('setting proKioskState to ' + enable);

        return $q(function(resolve, reject) {
            AveoKnox.setProKioskState(enable, passCode, function() {
                resolve();
            }, function(err) {
                log.error('failed setting proKioskState to ' + enable + ', error: ' + err);
                reject(err);
            });
        });
    }

    // function getHardKeyIntentState() {
    //     return $q(function(resolve, reject) {
    //         AveoKnox.getHardKeyIntentState(function(enabled) {
    //             resolve(enabled);
    //         }, function(err) {
    //             log.error('failed getting hardKeyIntentState, error: ' + err);
    //             reject(err);
    //         });
    //     });
    // }

    function setHardKeyIntentState(enable) {
        log.info('setting hardKeyIntentState to ' + enable);

        return $q(function(resolve, reject) {
            AveoKnox.setHardKeyIntentState(enable, function() {
                resolve();
            }, function(err) {
                log.error('failed setting hardKeyIntentState to ' + enable + ', error: ' + err);
                reject(err);
            });
        });
    }

    function setHideAllNotificationMessages(enable) {
        log.info('setting hideAllNotificationMessages to ' + enable);

        return $q(function(resolve, reject) {
            AveoKnox.setHideAllNotificationMessages(enable, function() {
                resolve();
            }, function(err) {
                log.error('failed setting hideAllNotificationMessages to ' + enable + ', error: ' + err);
                reject(err);
            });
        });
    }

    // function getHomeActivity() {
    //     return $q(function(resolve, reject) {
    //         AveoKnox.getHomeActivity(function(homePackage) {
    //             resolve(homePackage);
    //         }, function(err) {
    //             log.error('failed getting homeActivity, error: ' + err);
    //             reject(err);
    //         });
    //     });
    // }

    function setHomeActivity(homePackage) {
        log.info('setting homeActivity to ' + homePackage);

        return $q(function(resolve, reject) {
            AveoKnox.setHomeActivity(homePackage, function() {
                resolve();
            }, function(err) {
                log.error('failed setting homeActivity to ' + homePackage + ', error: ' + err);
                reject(err);
            });
        });
    }

    // function getInputMethodRestrictionState() {
    //     return $q(function(resolve, reject) {
    //         AveoKnox.getInputMethodRestrictionState(function(enabled) {
    //             resolve(enabled);
    //         }, function(err) {
    //             log.error('failed getting inputMethodRestrictionState, error: ' + err);
    //             reject(err);
    //         });
    //     });
    // }

    function setInputMethodRestrictionState(enable) {
        log.info('setting inputMethodRestrictionState to ' + enable);

        return $q(function(resolve, reject) {
            AveoKnox.setInputMethodRestrictionState(enable, function() {
                resolve();
            }, function(err) {
                log.error('failed setting inputMethodRestrictionState to ' + enable + ', error: ' + err);
                reject(err);
            });
        });
    }

    // function getPowerDialogOptionMode() {
    //     return $q(function(resolve, reject) {
    //         AveoKnox.getPowerDialogOptionMode(function(mode) {
    //             resolve(mode);
    //         }, function(err) {
    //             log.error('failed getting powerDialogOptionMode, error: ' + err);
    //             reject(err);
    //         });
    //     });
    // }

    function setPowerDialogOptionMode(mode) {
        return $q(function(resolve, reject) {
            AveoKnox.setPowerDialogOptionMode(mode, function() {
                resolve();
            }, function(err) {
                log.error('failed setting powerDialogOptionMode to ' + mode + ', error: ' + err);
                reject(err);
            });
        });
    }

    // function setProKioskString(stringType, stringValue) {
    //     log.info('setting proKioskString to ' + stringType + '/' + stringValue);

    //     return $q(function(resolve, reject) {
    //         AveoKnox.setProKioskString(stringType, stringValue, function() {
    //             resolve();
    //         }, function(err) {
    //             log.error('failed setting proKioskString to ' + stringType + '/' + stringValue + ', error: ' + err);
    //             reject(err);
    //         });
    //     });

    // }

    function setStatusBarHidden(enable) {
        log.info('setting statusBarHidden to ' + enable);

        return $q(function(resolve, reject) {
            AveoKnox.setStatusBarHidden(enable, function() {
                resolve();
            }, function(err) {
                log.error('failed setting statusBarHidden to ' + enable + ', error: ' + err);
                reject(err);
            });
        });
    }

    function setEthernetState(enable) {
        log.info('setting ethernet enabled to ' + enable);

        return $q(function(resolve, reject) {
            AveoKnox.setEthernetState(enable, function() {
                resolve();
            }, function(err) {
                // It is not uncommon to try to set the ethernet interface to
                // enabled, but not have an ethernet network present, so rather
                // than logging an error, we log a warning.
                log.warning('failed setting ethernet enabled to ' + enable + ', error: ' + err);
                reject(err);
            });
        });
    }

    function setInputMethod(className, force) {
        if (typeof className === 'undefined') {
            className = 'com.sec.android.inputmethod/.SamsungKeypad';
        }

        if (typeof force === 'undefined') force = true;

        log.info('setting inputMethod to ' + className + ' (force = ' + force + ')');

        return $q(function(resolve, reject) {
            AveoKnox.setInputMethod(className, force, function() {
                resolve();
            }, function(err) {
                log.error('failed setting inputMethod to ' + className + ', error: ' + err);
                reject(err);
            });
        });
    }

    // knox custom -> system

    // function getAutoRotationState() {
    //     return $q(function(resolve, reject) {
    //         AveoKnox.getAutoRotationState(function(enabled) {
    //             resolve(enabled);
    //         }, function(err) {
    //             log.error('failed getting autoRotationState, error: ' + err);
    //             reject(err);
    //         });
    //     });
    // }

    function setAutoRotationState(enable, rotation) {
        log.info('setting autoRotationState to ' + enable + '/' + rotation);

        return $q(function(resolve, reject) {
            AveoKnox.setAutoRotationState(enable, rotation, function() {
                resolve();
            }, function(err) {
                log.error('failed setting autoRotationState to ' + enable + '/' + rotation + ', error: ' + err);
                reject(err);
            });
        });
    }

    // function getChargerConnectionSoundEnabledState() {
    //     return $q(function(resolve, reject) {
    //         AveoKnox.getChargerConnectionSoundEnabledState(function(enabled) {
    //             resolve(enabled);
    //         }, function(err) {
    //             log.error('failed getting chargerConnectionSoundEnabledState, error: ' + err);
    //             reject(err);
    //         });
    //     });
    // }

    function setChargerConnectionSoundEnabledState(enable) {
        log.info('setting ChargerConnectionSoundEnabledState to ' + enable);

        return $q(function(resolve, reject) {
            AveoKnox.setChargerConnectionSoundEnabledState(enable, function() {
                resolve();
            }, function(err) {
                log.error('failed setting chargerConnectionSoundEnabledState to ' + enable + ', error: ' + err);
                reject(err);
            });
        });
    }

    function setForceAutoStartUpState(enable) {
        log.info('setting forceAutoStartUpState to ' + enable);

        return $q(function(resolve, reject) {
            AveoKnox.setForceAutoStartUpState(enable, function() {
                resolve();
            }, function(err) {
                log.error('failed setting forceAutoStartUpState, error: ' + err);
                reject(err);
            });
        });
    }

    // function getKeyboardMode() {
    //     return $q(function(resolve, reject) {
    //         AveoKnox.getKeyboardMode(function(mode) {
    //             resolve(mode);
    //         }, function(err) {
    //             log.error('failed getting keyboardMode, error: ' + err);
    //             reject(err);
    //         });
    //     });
    // }

    function setKeyboardMode(mode) {
        log.info('setting keyboardMode to ' + mode);

        return $q(function(resolve, reject) {
            AveoKnox.setKeyboardMode(mode, function() {
                resolve();
            }, function(err) {
                log.error('failed setting keyboardMode, error: ' + err);
                reject(err);
            });
        });
    }

    function setSystemSoundsEnabled(enable) {
        log.info('setting systemSoundsEnabled to ' + enable);

        return $q(function(resolve, reject) {
            AveoKnox.setSystemSoundsEnabled(enable, function() {
                resolve();
            }, function(err) {
                log.error('failed setting systemSoundsEnabled to ' + enable + ', error: ' + err);
                reject(err);
            });
        });
    }

    // function getToastEnabledState() {
    //     return $q(function(resolve, reject) {
    //         AveoKnox.getToastEnabledState(function(enabled) {
    //             resolve(enabled);
    //         }, function(err) {
    //             log.error('failed getting toastEnabledState, error: ' + err);
    //             reject(err);
    //         });
    //     });
    // }

    function setToastEnabledState(enable) {
        log.info('setting toastEnabledState to ' + enable);

        return $q(function(resolve, reject) {
            AveoKnox.setToastEnabledState(enable, function() {
                resolve();
            }, function(err) {
                log.error('failed setting toastEnabledState to ' + enable + ', error: ' + err);
                reject(err);
            });
        });
    }

    function removeLockScreen() {
        log.info('setting removeLockScreen');

        return $q(function(resolve, reject) {
            AveoKnox.removeLockScreen(function() {
                resolve();
            }, function(err) {
                log.error('failed setting removeLockScreen, error: ' + err);
                reject(err);
            });
        });
    }

    function updateKnoxAnimations(update, onProgress) {
        var bootLoopAnimName = 'bootLoopAnim.qmg';
        var bootSoundName = 'bootSound.ogg';
        var bootupAnimName = 'bootupAnim.qmg';
        var shutdownAnimName = 'shutdownAnim.qmg';
        var shutdownSoundName = 'shutdownSound.ogg';

        var bootLoopAnimDest = cordova.file.dataDirectory + bootLoopAnimName;
        var bootSoundDest = cordova.file.dataDirectory + bootSoundName;
        var bootupAnimDest = cordova.file.dataDirectory + bootupAnimName;
        var shutdownAnimDest = cordova.file.dataDirectory + shutdownAnimName;
        var shutdownSoundDest = cordova.file.dataDirectory + shutdownSoundName;

        return $q.resolve()
        .then(function() {
            return FileDownloader.download(update.bootLoopAnim, bootLoopAnimDest, function(prog) {
                onProgress(0.2 * prog);
            });
        })
        .then(function() {
            return FileDownloader.download(update.bootSound, bootSoundDest, function(prog) {
                onProgress(0.2 + 0.2 * prog);
            });
        })
        .then(function() {
            return FileDownloader.download(update.bootupAnim, bootupAnimDest, function(prog) {
                onProgress(0.4 + 0.2 * prog);
            });
        })
        .then(function() {
            return FileDownloader.download(update.shutdownAnim, shutdownAnimDest, function(prog) {
                onProgress(0.6 + 0.2 * prog);
            });
        })
        .then(function() {
            return FileDownloader.download(update.shutdownSound, shutdownSoundDest, function(prog) {
                onProgress(0.8 + 0.2 * prog);
            });
        })
        .then(function() {
            return $q(function(resolve, reject) {
                AveoKnox.updateBootAnimations(bootupAnimName, bootLoopAnimName, bootSoundName,
                    function() {
                        resolve();
                    }, function(errorMessage) {
                        log.error('error updating boot animations: ' + errorMessage);
                        reject(errorMessage);
                    }
                );
            });
        })
        .then(function() {
            return $q(function(resolve, reject) {
                AveoKnox.updateShutdownAnimation(shutdownAnimName, shutdownSoundName,
                    function() {
                        resolve();
                    }, function(errorMessage) {
                        log.error('error updating shutdown animations: ' + errorMessage);
                        reject(errorMessage);
                    }
                );
            });
        });
    }

    function cleanUpFiles() {
        var cleanUpInfo = [
            { path: cordova.file.dataDirectory, fileRegExp: /\.qmg|\.ogg$/ }, // knoxAnimations
            { path: cordova.file.externalDataDirectory, fileRegExp: /\.apk$/ }, // apks
        ];

        var cleanUpPromises = [];

        cleanUpInfo.forEach(function(cui) {
            cleanUpPromises.push($q(function(resolve, reject) {
                window.resolveLocalFileSystemURL(cui.path, function(dirEntry) {
                    removeFilesInDirectory(dirEntry, cui.fileRegExp)
                    .then(resolve, reject);
                });
            }));
        });

        return $q.all(cleanUpPromises);
    }

    function removeFilesInDirectory(dirEntry, removeRegExp) {
        var removalPromises = [];
        var dirReader = dirEntry.createReader();

        return $q(function(resolve, reject) {
            dirReader.readEntries(function(entries) {
                for (var i = 0; i < entries.length; i++) {
                    var entry = entries[i];

                    if (!entry.isDirectory && removeRegExp.exec(entry.fullPath)) {
                        removalPromises.push(removeFile(entry));
                    }
                }

                $q.all(removalPromises).then(resolve, resolve);
            }, function() {
                log.error('failed reading files in ' + dirEntry.fullPath);
                resolve();
            });
        });
    }

    function removeFile(fileEntry) {
        return $q(function(resolve, reject) {
            log.debug('cleaning up ' + fileEntry.fullPath);
            fileEntry.remove(function() {
                resolve();
            }, function() {
                log.error('failed removing ' + fileEntry.fullPath);
                resolve();
            });
        });
    }
}

module.exports = KnoxService;
